export enum BdcPropertyPricingType {
  STANDARD = 'Standard',
  LOS = 'LOS',
  OBP = 'OBP',
}

export enum BdcPetsAllowedEnum {
  PETS_NOT_ALLOWED = 'PETS_NOT_ALLOWED',
  PETS_ALLOWED = 'PETS_ALLOWED',
  PETS_ALLOWED_ON_REQUEST = 'PETS_ALLOWED_ON_REQUEST',
}

export enum BdcPetsPriceType {
  FREE = 'FREE',
  CHARGES_MAY_APPLY = 'CHARGES_MAY_APPLY',
}

export enum BdcDamagePolicyType {
  NONE = 'NONE',
  HANDLED_BY_PROPERTY = 'HANDLED_BY_PROPERTY',
  HANDLED_BY_BOOKING_COM = 'HANDLED_BY_BOOKING_COM',
}

export enum BdcDamageCollectionMode {
  CASH = 'CASH',
  CREDIT_CARD = 'CREDIT_CARD',
  PAYPAL = 'PAYPAL',
  BANK_TRANSFER = 'BANK_TRANSFER',
  OTHER = 'OTHER',
}

export enum BdcStandardPhrase {
  GuestIdentification = 'GuestIdentification',
  InformArrivalTime = 'InformArrivalTime',
  PayBeforeStay = 'PayBeforeStay',
  RequireInvoice = 'RequireInvoice',
}

export enum BdcCancellationGracePeriodEnum {
  HOURS_1 = 'HOURS_1',
  HOURS_4 = 'HOURS_4',
  HOURS_24 = 'HOURS_24',
}

export enum BdcBookingModelType {
  IB = 'IB',
  RTB = 'RTB',
}

export interface BdcPetsPolicy {
  pets_allowed: BdcPetsAllowedEnum
  pets_price_type?: BdcPetsPriceType
}

export enum BdcDamagePolicyCollectionWhen {
  ON_ARRIVAL = 'ON_ARRIVAL',
  SEVEN_DAYS_BEFORE_ARRIVAL = 'SEVEN_DAYS_BEFORE_ARRIVAL',
  FOURTEEN_DAYS_BEFORE_ARRIVAL = 'FOURTEEN_DAYS_BEFORE_ARRIVAL',
}

export enum BdcDamagePolicyReturnWhen {
  ON_CHECKOUT = 'ON_CHECKOUT',
  WITHIN_7_DAYS = 'WITHIN_7_DAYS',
  WITHIN_14_DAYS = 'WITHIN_14_DAYS',
}

export interface BdcDamagePolicy {
  amount?: number
  currency?: string
  policy_type: BdcDamagePolicyType
  damage_programme_terms_agreed?: boolean
  collection_mode?: BdcDamageCollectionMode
  collection_when?: BdcDamagePolicyCollectionWhen
  return_mode?: BdcDamageCollectionMode
  return_when?: BdcDamagePolicyReturnWhen
}

export interface BdcPropertySettings {
  require_cvc?: boolean
  require_booker_address?: boolean
  require_booker_phone_number?: boolean
  auto_replenish?: boolean
  replenish_closed_rooms?: boolean
  pricing_type?: BdcPropertyPricingType
  allow_smoking?: boolean
  view_cc_details?: boolean
}

export interface BdcPropertySettingsAndPolicies {
  property_settings?: BdcPropertySettings
  pets?: BdcPetsPolicy
  damage_policy?: BdcDamagePolicy
  standard_phrases?: BdcStandardPhraseData[]
  quiet_hours?: BdcQuietHours
  renovation?: BdcRenovation
  bed_linen_policy?: BdcBedLinenPolicy
  accepted_payment_types?: BdcAcceptedPaymentTypes
  french_tax_details?: BdcFrenchTaxDetails
  cancellation_exceptions?: BdcCancellationExceptions
  age_buckets?: BdcAgeBucketData[]
  children_policies?: BdcChildrenPolicies
  booking_model?: BdcBookingModel
  invoice_settings?: BdcInvoiceSettings
}

export interface BdcStandardPhraseData {
  name: BdcStandardPhrase
  enabled: boolean
}

export interface BdcAgeRestriction {
  enabled?: boolean
  min?: number
  max?: number
}

export interface BdcQuietHours {
  enabled: boolean
  start?: string // Format: HH:MM
  end?: string // Format: HH:MM
}

export interface BdcRenovation {
  enabled: boolean
  start?: string // Format: YYYY-MM-DD
  end?: string // Format: YYYY-MM-DD
}

export interface BdcBedLinenPolicy {
  enabled: boolean
  amount?: number
}

export interface BdcAcceptedPaymentTypes {
  codes?: number[]
}

export interface BdcFrenchTaxDetails {
  declare_revenue: boolean
  has_vat: boolean
  registered_in_rcs: boolean
}

export enum BdcAdvanceCancellationBeforeCheckInEnum {
  WEEKS_4 = 'WEEKS_4',
  WEEKS_8 = 'WEEKS_8',
  WEEKS_12 = 'WEEKS_12',
}

export interface BdcAdvanceCancellationSettings {
  enabled: boolean
  before_check_in?: BdcAdvanceCancellationBeforeCheckInEnum
}

export interface BdcCancellationExceptions {
  grace_period?: BdcCancellationGracePeriod
  advance_cancellation?: BdcAdvanceCancellationSettings
}

export interface BdcCancellationGracePeriod {
  enabled?: boolean
  after_booking?: BdcCancellationGracePeriodEnum
}

export interface BdcAgeBucketData {
  min_age: number
  max_age: number
}

export interface BdcChildrenPolicies {
  allow_children: boolean
  min_age: number
}

export interface BdcBookingModel {
  type: 'IB' | 'RTB'
}

export enum BdcNotificationChannel {
  POSTAL_MAIL = 'POSTAL_MAIL',
  EMAIL = 'EMAIL',
}

export enum BdcTaxPayerNumberType {
  CNPJ = 'CNPJ', // CNPJ (company)
  CPF = 'CPF', // CPF (private individual).
}

export interface BdcBrazilTaxDetails {
  tax_payer_number_type: BdcTaxPayerNumberType
  tax_payer_number: string
  city_hall_id: string
  email: string
}

export interface BdcInvoiceSettings {
  legal_name?: string
  contact_person?: string
  address?: string
  country_code?: string
  city?: string
  postal_code?: string
  state?: string
  notification_channel?: BdcNotificationChannel
  brazil_tax_details?: BdcBrazilTaxDetails
}

export interface BdcCurfew {
  enabled?: boolean
  start?: string // Format: HH:MM
  end?: string // Format: HH:MM
}
